/**
 * GA4 Advanced Analytics Module
 * Provides: Error Tracking, Web Vitals, Engagement Timer,
 * Outbound Link Clicks, Cart Abandonment Timing
 */

// ─── Helper: safe gtag call ───
function gtag(...args: unknown[]) {
  const g = (window as any).gtag;
  if (typeof g === 'function') g(...args);
}

// ═══════════════════════════════════════════════
// 1. JS Error Tracking → GA4 exception events
// ═══════════════════════════════════════════════
let errorTrackingInitialized = false;

export function initErrorTracking() {
  if (errorTrackingInitialized) return;
  errorTrackingInitialized = true;

  window.addEventListener('error', (e) => {
    gtag('event', 'exception', {
      description: `${e.message} @ ${e.filename}:${e.lineno}`,
      fatal: false,
      error_type: 'js_error',
      page_path: window.location.pathname,
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const msg = e.reason?.message || e.reason?.toString?.() || 'Unknown promise rejection';
    gtag('event', 'exception', {
      description: msg.slice(0, 150),
      fatal: false,
      error_type: 'unhandled_promise',
      page_path: window.location.pathname,
    });
  });
}

// ═══════════════════════════════════════════════
// 2. Web Vitals → GA4 custom events
// ═══════════════════════════════════════════════
export function initWebVitals() {
  if (typeof PerformanceObserver === 'undefined') return;

  // LCP (Largest Contentful Paint)
  try {
    const lcpObserver = new PerformanceObserver((list) => {
      const entries = list.getEntries();
      const lastEntry = entries[entries.length - 1] as any;
      if (lastEntry) {
        gtag('event', 'web_vitals', {
          metric_name: 'LCP',
          metric_value: Math.round(lastEntry.startTime),
          metric_rating: lastEntry.startTime < 2500 ? 'good' : lastEntry.startTime < 4000 ? 'needs_improvement' : 'poor',
          page_path: window.location.pathname,
        });
      }
    });
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch {}

  // CLS (Cumulative Layout Shift)
  try {
    let clsValue = 0;
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as any[]) {
        if (!entry.hadRecentInput) {
          clsValue += entry.value;
        }
      }
    });
    clsObserver.observe({ type: 'layout-shift', buffered: true });

    // Report CLS on page hide
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        gtag('event', 'web_vitals', {
          metric_name: 'CLS',
          metric_value: Math.round(clsValue * 1000) / 1000,
          metric_rating: clsValue < 0.1 ? 'good' : clsValue < 0.25 ? 'needs_improvement' : 'poor',
          page_path: window.location.pathname,
        });
      }
    }, { once: true });
  } catch {}

  // FID / INP (First Input Delay / Interaction to Next Paint)
  try {
    const fidObserver = new PerformanceObserver((list) => {
      const entry = list.getEntries()[0] as any;
      if (entry) {
        gtag('event', 'web_vitals', {
          metric_name: 'FID',
          metric_value: Math.round(entry.processingStart - entry.startTime),
          metric_rating: (entry.processingStart - entry.startTime) < 100 ? 'good' : 'needs_improvement',
          page_path: window.location.pathname,
        });
      }
    });
    fidObserver.observe({ type: 'first-input', buffered: true });
  } catch {}
}

// ═══════════════════════════════════════════════
// 3. Engagement Timer — time on page before leave
// ═══════════════════════════════════════════════
let engagementStart = 0;
let currentPagePath = '';

export function startEngagementTimer(pagePath: string) {
  // Report previous page engagement
  if (engagementStart > 0 && currentPagePath) {
    const duration = Math.round((Date.now() - engagementStart) / 1000);
    if (duration >= 3) { // Only report if > 3 seconds
      gtag('event', 'page_engagement', {
        engagement_time_sec: duration,
        page_path: currentPagePath,
        engaged: duration >= 10,
      });
    }
  }
  engagementStart = Date.now();
  currentPagePath = pagePath;
}

// Report on page unload
if (typeof window !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && engagementStart > 0 && currentPagePath) {
      const duration = Math.round((Date.now() - engagementStart) / 1000);
      if (duration >= 3) {
        // Use sendBeacon-compatible approach
        gtag('event', 'page_engagement', {
          engagement_time_sec: duration,
          page_path: currentPagePath,
          engaged: duration >= 10,
        });
      }
    }
  });
}

// ═══════════════════════════════════════════════
// 4. Outbound Link Click Tracking
// ═══════════════════════════════════════════════
let outboundTrackingInitialized = false;

export function initOutboundLinkTracking() {
  if (outboundTrackingInitialized) return;
  outboundTrackingInitialized = true;

  document.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement).closest('a');
    if (!link) return;

    const href = link.href;
    if (!href) return;

    try {
      const url = new URL(href);
      if (url.hostname === window.location.hostname) return;

      gtag('event', 'click', {
        event_category: 'outbound',
        event_label: url.hostname,
        link_url: href,
        link_text: (link.textContent || '').trim().slice(0, 100),
        page_path: window.location.pathname,
        transport_type: 'beacon',
      });
    } catch {}
  });
}

// ═══════════════════════════════════════════════
// 5. File Download Tracking
// ═══════════════════════════════════════════════
let downloadTrackingInitialized = false;
const DOWNLOAD_EXTENSIONS = /\.(pdf|xlsx?|docx?|csv|zip|rar|7z|pptx?|png|jpg|jpeg|gif|svg|webp)$/i;

export function initFileDownloadTracking() {
  if (downloadTrackingInitialized) return;
  downloadTrackingInitialized = true;

  document.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement).closest('a');
    if (!link?.href) return;

    const match = link.href.match(DOWNLOAD_EXTENSIONS);
    if (!match) return;

    gtag('event', 'file_download', {
      file_extension: match[1].toLowerCase(),
      file_name: link.href.split('/').pop() || 'unknown',
      link_url: link.href,
      page_path: window.location.pathname,
    });
  });
}

// ═══════════════════════════════════════════════
// 6. Cart Abandonment Timing
// ═══════════════════════════════════════════════
const CART_OPEN_KEY = 'ga4_cart_open_ts';

export function trackCartOpen() {
  if (!sessionStorage.getItem(CART_OPEN_KEY)) {
    sessionStorage.setItem(CART_OPEN_KEY, Date.now().toString());
  }
}

export function trackCartAbandonment() {
  const openTs = sessionStorage.getItem(CART_OPEN_KEY);
  if (!openTs) return;

  const durationSec = Math.round((Date.now() - parseInt(openTs)) / 1000);
  gtag('event', 'cart_abandonment', {
    time_in_cart_sec: durationSec,
    page_path: window.location.pathname,
  });
}

export function clearCartTimer() {
  sessionStorage.removeItem(CART_OPEN_KEY);
}

// ═══════════════════════════════════════════════
// 7. Scroll Depth Tracking (25%, 50%, 75%, 100%)
// ═══════════════════════════════════════════════
let scrollTrackingActive = false;
let scrollThresholdsHit = new Set<number>();
const SCROLL_THRESHOLDS = [25, 50, 75, 100];

export function startScrollDepthTracking(pagePath: string) {
  // Reset thresholds for new page
  scrollThresholdsHit = new Set<number>();

  if (scrollTrackingActive) return;
  scrollTrackingActive = true;

  const handleScroll = () => {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const docHeight = document.documentElement.scrollHeight - document.documentElement.clientHeight;
    if (docHeight <= 0) return;

    const percent = Math.round((scrollTop / docHeight) * 100);

    for (const threshold of SCROLL_THRESHOLDS) {
      if (percent >= threshold && !scrollThresholdsHit.has(threshold)) {
        scrollThresholdsHit.add(threshold);
        gtag('event', 'scroll_depth', {
          percent_scrolled: threshold,
          page_path: pagePath,
        });
      }
    }
  };

  // Use passive listener for performance
  window.addEventListener('scroll', handleScroll, { passive: true });
}

// ═══════════════════════════════════════════════
// 8. Initialize all GA4 advanced features
// ═══════════════════════════════════════════════
let allInitialized = false;

export function initAllGA4Advanced() {
  if (allInitialized) return;
  allInitialized = true;

  initErrorTracking();
  initWebVitals();
  initOutboundLinkTracking();
  initFileDownloadTracking();
}
